import React, { useState, useRef, useEffect } from "react";
import { Modal, Checkbox, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { CloseOutlined } from "@ant-design/icons";
import { AGREEMENT_CONTENT, AgreementSection } from "../../utilities/constants/agreementContent";
import { acceptAgreement } from "../../utilities/helpers/agreementHelper";

interface AgreementModalProps {
  visible: boolean;
  onAccept: () => void;
  onDecline?: () => void;
}

const AgreementModal: React.FC<AgreementModalProps> = ({ visible, onAccept, onDecline }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleClose = () => {
    navigate(-1);
  };

  // Reset state when modal opens and check if scrolling is needed
  useEffect(() => {
    if (visible) {
      setHasScrolledToBottom(false);
      setIsChecked(false);

      // Check if content needs scrolling
      setTimeout(() => {
        if (contentRef.current) {
          const { scrollHeight, clientHeight } = contentRef.current;
          // If content doesn't need scrolling, enable the checkbox immediately
          if (scrollHeight <= clientHeight + 10) {
            setHasScrolledToBottom(true);
          }
        }
      }, 100);
    }
  }, [visible]);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Check if user has scrolled to bottom (with 50px tolerance)
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleAccept = () => {
    acceptAgreement();
    onAccept();
  };

  const renderSection = (section: AgreementSection, level: number = 0) => {
    return (
      <div key={section.title} className={`${level > 0 ? "ml-4" : ""} mb-4`}>
        <h4 className={`font-semibold text-gray-800 ${level === 0 ? "text-base mb-2" : "text-sm mb-1"}`}>{section.title}</h4>
        {section.content && (
          <div className="text-sm leading-relaxed text-gray-600">
            {Array.isArray(section.content) ? (
              section.content.map((line, idx) => (
                <p key={idx} className={line.startsWith("•") ? "ml-4" : "mb-2"}>
                  {line}
                </p>
              ))
            ) : (
              <p>{section.content}</p>
            )}
          </div>
        )}
        {section.subsections?.map((sub) => renderSection(sub, level + 1))}
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={600}
      className="agreement-modal *:*:!p-0 rounded-xl overflow-hidden"
      styles={{
        body: { padding: 0 },
      }}
    >
      <div className="flex flex-col max-h-[90dvh] ">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary/5">
          <h2 className="m-0 text-xl font-semibold text-gray-900">{AGREEMENT_CONTENT.title}</h2>
          <button onClick={handleClose} className="flex items-center justify-center w-8 h-8 text-gray-500 transition-colors rounded-lg hover:bg-gray-200 hover:text-gray-700">
            <CloseOutlined className="text-lg" />
          </button>
        </div>

        <div ref={contentRef} onScroll={handleScroll} className="flex-1 p-3 overflow-y-auto" style={{ maxHeight: "50vh" }}>
          <div className="p-4 mb-2 rounded-lg bg-primary/10">
            <p className="p-0 m-0 text-xs leading-relaxed text-gray-700">{AGREEMENT_CONTENT.introduction}</p>
          </div>

          {AGREEMENT_CONTENT.sections.map((section) => renderSection(section))}

          <div className="p-4 mt-4 rounded-lg bg-gray-50">
            <p className="m-0 text-sm text-gray-600">
              If you have questions, contact us at:{" "}
              <a href={`mailto:${AGREEMENT_CONTENT.contactInfo.email}`} className="font-medium text-primary hover:underline">
                {AGREEMENT_CONTENT.contactInfo.email}
              </a>
            </p>
          </div>

          <div className="flex items-start gap-3 mt-4 mb-4">
            <Checkbox checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} disabled={!hasScrolledToBottom} className="mt-1" />
            <label className={`text-sm cursor-pointer select-none ${hasScrolledToBottom ? "text-gray-700" : "text-gray-400"}`} onClick={() => hasScrolledToBottom && setIsChecked(!isChecked)}>
              I confirm and agree to the Terms & Privacy Notice.
            </label>
          </div>
        </div>

        <div className="p-3 border-t border-gray-200 bg-gray-50">
          {!hasScrolledToBottom && (
            <div className="flex items-center justify-center gap-2 mb-3 text-sm text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              <span>Please scroll to the bottom to continue</span>
            </div>
          )}

          <div className="flex gap-3">
            {onDecline && (
              <Button size="large" className="flex-1" onClick={onDecline}>
                Decline
              </Button>
            )}
            <Button type="primary" size="large" className={`${onDecline ? "flex-1" : "w-full"}`} disabled={!isChecked || !hasScrolledToBottom} onClick={handleAccept}>
              I Agree
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AgreementModal;
